<template>
  <div class="media-container" :class="moreClass">
    <template v-if="isDrive">
      <img
        v-if="snapshot && !imageLoadError"
        class="rounded-lg"
        :width="width"
        :height="height"
        :src="createThumbnailUrl(media)"
        @error="handleImageError"
      />
      <div
        v-else-if="snapshot && imageLoadError"
        class="image-error-fallback rounded-lg"
        :style="{ width: width + 'px', height: height + 'px' }"
      >
        <v-icon size="large">mdi-google-drive</v-icon>
      </div>
      <div class="center-all" style="height: 100%" v-else>
        <iframe
          :width="isMobile ? '300' : '560'"
          :height="isMobile ? '160' : '315'"
          :src="createPreviewUrl(media)"
          frameborder="0"
          allowfullscreen
          class="drive-iframe"
        />
      </div>
    </template>
    <template v-else-if="isImage && (IsNotCloudinary || isVuetifyImage)">
      <v-img :src="media" :max-width="maxWidth" :max-height="maxHeight" />
    </template>
    <template v-else-if="isImage">
      <cloudinary
        class="v-image v-responsive rounded-lg cld"
        :src="media"
        :width="width"
        :height="height"
      />
    </template>
    <template v-else-if="isVideo && !IsNotCloudinary">
      <cloudinary
        class="v-image v-responsive rounded-lg cld"
        :src="media"
        type="video"
        :snapshot="snapshot"
        :width="width"
        :height="height"
      />
    </template>
    <template v-else-if="isVideo && IsNotCloudinary">
      <video
        :src="media"
        style="width: 100%; height: 100%; object-fit: cover"
        :controls="shouldShowVideoControls"
        class="rounded-lg"
        ref="video"
      ></video>
    </template>
    <template v-else-if="isAudio && !snapshot">
      <div class="audio-container">
        <audio :src="media" controls></audio>
      </div>
    </template>
    <template v-else-if="isAudio && snapshot">
      <div :style="snapshotStyle">
        <v-icon>mdi-music-box</v-icon>
      </div>
    </template>
    <div class="overlay">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import {
  extractFileId,
  createThumbnailUrl,
  createPreviewUrl,
} from '@/helpers/googleDrive'
import { useAuthStore } from '@/store/auth'

export default {
  name: 'Media',
  props: {
    snapshot: Boolean,
    media: {
      type: String,
      required: true,
    },
    isVuetifyImage: {
      type: Boolean,
      default: false,
    },
    mediaType: {
      type: String,
      required: true,
      validator: value => ['image', 'video', 'audio'].includes(value),
    },
    height: {
      type: [String, Number],
      default: '200',
    },
    width: {
      type: [String, Number],
      default: '250',
    },
    maxHeight: {
      type: [String, Number],
      default: '250',
    },
    maxWidth: {
      type: [String, Number],
      default: '250',
    },
    moreClass: {
      type: String,
      default: '',
    },
  },
  setup() {
    const { isAdmin } = storeToRefs(useAuthStore())
    return {
      isAdmin,
    }
  },
  data() {
    return {
      imageLoadError: false,
    }
  },
  computed: {
    isDrive() {
      return this.media.includes('drive.google.com')
    },
    IsNotCloudinary() {
      return !this.media.includes('cloudinary')
    },
    isImage() {
      return this.mediaType === 'image'
    },
    isVideo() {
      return this.mediaType === 'video'
    },
    isAudio() {
      return this.mediaType === 'audio'
    },
    shouldShowVideoControls() {
      return !this.snapshot
    },
    snapshotStyle() {
      return {
        width: '100%',
        display: 'grid',
        placeItems: 'center',
        height: `${this.height}px`,
      }
    },
    isMobile() {
      return this.$vuetify?.breakpoint?.smAndDown || false
    },
  },
  methods: {
    extractGoogleDriveFileId(url) {
      return extractFileId(url)
    },
    createThumbnailUrl() {
      if (!this.isDrive) return
      const fileId = this.extractGoogleDriveFileId(this.media)
      return createThumbnailUrl(fileId)
    },
    createPreviewUrl() {
      if (!this.isDrive) return
      const fileId = this.extractGoogleDriveFileId(this.media)
      return createPreviewUrl(fileId)
    },
    handleImageError() {
      this.imageLoadError = true
    },
  },
  mounted() {
    if (this.isVideo && this.snapshot) {
      this.$refs.video?.addEventListener?.('loadeddata', () => {
        this.$refs.video.currentTime = this.$refs.video.duration / 2
        this.$refs.video.pause()
      })
    } else {
      this.$refs.video?.addEventListener?.('loadeddata', () => {
        let isTooLong
        const maxDuration = this.isAdmin ? 120 : 30
        if (this.$refs.video.duration > maxDuration) isTooLong = true
        else isTooLong = false
        this.$emit('videoTooLong', isTooLong)
      })
    }
  },
}
</script>

<style scoped>
.cld {
  width: 100%;
  height: 100%;
}

.media-container {
  position: relative;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  /* display: flex;
  justify-content: center; */
}

.audio-container,
.snapshot-container {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
}

.overlay :deep(.media-title) {
  position: absolute;
  bottom: 0;
  width: 100%;
  display: flex;
  background-color: rgba(255, 255, 255, 0.4);
  color: black;
}

.overlay :deep(.open-dialog-icon),
.edit {
  position: absolute;
  top: 5px;
}

.drive-iframe {
  border-radius: 12px;
}

.image-error-fallback {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  color: #666;
  text-align: center;
  padding: 1rem;
}
</style>
